import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { addDagen, compactChangeNr, FILTER_STATUSSEN, fmtUren, maandagVan, matchtFilter, type FilterStatus } from '@ep/core';
import { useData, useTaakOpenen } from '../lib/data';
import { TakenTabel, urenVan } from '../ui/TakenTabel';
import { Laden, MultiSelect, Veld } from '../ui/ui';

type Periode = 'alles' | 'week' | 'volgende' | 'maand' | 'verleden' | 'toekomst';

export function Taken() {
  const { data: d } = useData();
  const [zoek, setZoek] = useState('');
  const [proj, setProj] = useState<string[]>([]);
  const [klant, setKlant] = useState<string[]>([]);
  const [eng, setEng] = useState<string[]>([]);
  const [zp] = useSearchParams();
  const taak = useTaakOpenen();
  const [status, setStatus] = useState<FilterStatus | 'Alle statussen'>(() => { const q = zp.get('status'); return q && (FILTER_STATUSSEN as string[]).includes(q) ? (q as FilterStatus) : 'Alle statussen'; });
  const [periode, setPeriode] = useState<Periode>('alles');

  const rijen = useMemo(() => {
    if (!d) return [];
    const z = zoek.toLowerCase();
    const zc = compactChangeNr(zoek);
    const ma = maandagVan(d.vandaag);
    const [van, tot] =
      periode === 'week' ? [ma, addDagen(ma, 6)] : periode === 'volgende' ? [addDagen(ma, 7), addDagen(ma, 13)] : periode === 'maand' ? [d.vandaag.slice(0, 8) + '01', d.vandaag.slice(0, 8) + '31'] : periode === 'verleden' ? ['0000', addDagen(d.vandaag, -1)] : periode === 'toekomst' ? [d.vandaag, '9999'] : [null, null];
    const eNaam = (id?: string | null) => d.engineers.find((e) => e.id === id)?.naam ?? '';
    return d.taken
      .filter((t) => !z || [t.titel, t.klant, t.projectnummer, eNaam(t.engineerId)].some((x) => x?.toLowerCase().includes(z)) || (zc.length > 3 && compactChangeNr(t.changeNr).includes(zc)))
      .filter((t) => !proj.length || proj.includes(t.projectId ?? ''))
      .filter((t) => !klant.length || klant.includes(t.klant ?? ''))
      .filter((t) => !eng.length || eng.includes(t.engineerId ?? ''))
      .filter((t) => matchtFilter(t, status, d.vandaag))
      .filter((t) => !van || (t.startdatum && (t.einddatum ?? t.startdatum) >= van && t.startdatum <= tot!))
      .sort((a, b) => (b.startdatum ?? '9999').localeCompare(a.startdatum ?? '9999'));
  }, [d, zoek, proj, klant, eng, status, periode]);

  if (!d) return <Laden />;
  const totaal = rijen.reduce((s, t) => s + urenVan(t), 0);
  const gepland = rijen.filter((t) => t.status === 'GEPLAND').length;
  return (
    <div className="pagina kolom" style={{ gap: 12 }}>
      <div className="paneel kolom">
        <div className="rij wrap">
          <h1>Taken</h1>
          <span className="mute">
            {rijen.length} taken · {gepland} gepland · {rijen.length - gepland} nog niet gepland · {fmtUren(totaal)} uur
          </span>
          <div className="groei" />
          <button className="knop" onClick={() => { setZoek(''); setProj([]); setKlant([]); setEng([]); setStatus('Alle statussen'); setPeriode('alles'); }}>Filters wissen</button>
          <Link className="knop primair" to="/taken/nieuw?type=OVERLEG" state={taak.state}>+ Overleg</Link>
          <Link className="knop primair" to="/verlof">+ Overig</Link>
          <Link className="knop primair" to="/taken/nieuw?type=SERVICE_CALL" state={taak.state}>+ Service call</Link>
          <Link className="knop primair" to="/taken/nieuw" state={taak.state}>+ Nieuwe taak</Link>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 1fr 1fr 220px 220px', gap: 10 }}>
          <Veld label="Zoeken"><input value={zoek} onChange={(e) => setZoek(e.target.value)} placeholder="Taak, project, change, klant, engineer" /></Veld>
          <Veld label="Projectnummer"><MultiSelect opties={d.projecten.map((p) => ({ waarde: p.id, label: `${p.nummer} ${p.naam}` }))} waarden={proj} onChange={setProj} placeholder="Alle projecten" /></Veld>
          <Veld label="Klant"><MultiSelect opties={[...new Set(d.taken.map((t) => t.klant).filter(Boolean) as string[])].sort().map((x) => ({ waarde: x, label: x }))} waarden={klant} onChange={setKlant} placeholder="Alle klanten" /></Veld>
          <Veld label="Engineer"><MultiSelect opties={d.engineers.map((e) => ({ waarde: e.id, label: e.naam }))} waarden={eng} onChange={setEng} placeholder="Alle engineers" /></Veld>
          <Veld label="Status">
            <select value={status} onChange={(e) => setStatus(e.target.value as FilterStatus)}>
              <option>Alle statussen</option>
              {FILTER_STATUSSEN.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Veld>
          <Veld label="Periode">
            <select value={periode} onChange={(e) => setPeriode(e.target.value as Periode)}>
              <option value="alles">Alles</option>
              <option value="week">Deze week</option>
              <option value="volgende">Volgende week</option>
              <option value="maand">Deze maand</option>
              <option value="toekomst">Vanaf vandaag</option>
              <option value="verleden">Verleden</option>
            </select>
          </Veld>
        </div>
      </div>
      <div className="paneel" style={{ padding: 0, overflow: 'auto' }}>
        <TakenTabel taken={rijen} />
        {!rijen.length && <div className="leeg">Geen taken gevonden.</div>}
      </div>
    </div>
  );
}
